import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Loader2, Save, GripVertical } from "lucide-react";
import { toast } from "sonner";

export default function AdminRegForm() {
  const [form, setForm] = useState({ title: "", description: "", fields: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/reg-form").then((r) => setForm(r.data)).catch(() => {}); }, []);

  const addField = () => {
    const id = `field_${Date.now()}`;
    setForm({ ...form, fields: [...form.fields, { id, name: id, label: "Nowe pole", type: "text", required: false, options: [] }] });
  };
  const updateField = (i, patch) => {
    const fields = [...form.fields];
    fields[i] = { ...fields[i], ...patch };
    setForm({ ...form, fields });
  };
  const removeField = (i) => setForm({ ...form, fields: form.fields.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/reg-form", form);
      toast.success("Formularz zapisany");
    } catch { toast.error("Błąd zapisu"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Kreator formularza rejestracji</h2>
          <p className="mt-1 text-sm text-muted-foreground">Pola e-mail, hasło i imię są stałe. Dodaj własne pola profilowe.</p>
        </div>
        <Button onClick={save} disabled={saving} className="gap-1.5 bg-ztk-blue" data-testid="regform-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Zapisz
        </Button>
      </div>

      <div className="mt-8 max-w-2xl space-y-4">
        <div className="rounded-2xl border border-border bg-card p-6">
          <div><Label>Tytuł formularza</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="regform-title" className="mt-1.5" /></div>
          <div className="mt-4"><Label>Opis</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="regform-desc" className="mt-1.5" /></div>
        </div>

        <div className="space-y-3">
          {(form.fields || []).map((f, i) => (
            <div key={f.id} className="rounded-xl border border-border bg-card p-4" data-testid={`regform-field-${i}`}>
              <div className="flex items-center gap-2">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
                <Input value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} placeholder="Etykieta" className="flex-1" data-testid={`regform-field-label-${i}`} />
                <select value={f.type} onChange={(e) => updateField(i, { type: e.target.value })} className="h-10 rounded-md border border-input bg-background px-2 text-sm" data-testid={`regform-field-type-${i}`}>
                  <option value="text">Tekst</option>
                  <option value="number">Liczba</option>
                  <option value="email">E-mail</option>
                </select>
                <Button size="icon" variant="ghost" onClick={() => removeField(i)} className="text-destructive" data-testid={`regform-field-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="mt-2 flex items-center gap-2 pl-6">
                <Switch checked={f.required} onCheckedChange={(v) => updateField(i, { required: v })} data-testid={`regform-field-required-${i}`} />
                <span className="text-sm text-muted-foreground">Pole wymagane</span>
              </div>
            </div>
          ))}
        </div>
        <Button variant="outline" onClick={addField} className="gap-1.5" data-testid="admin-add-field-button"><Plus className="h-4 w-4" /> Dodaj pole</Button>
      </div>
    </div>
  );
}
